import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Card,
  Drawer,
  Tag,
  Avatar,
  notification,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShopOutlined,
  FileTextOutlined,
  BarcodeOutlined
} from '@ant-design/icons';
import { vendorService } from '../../services/vendorService';
import VendorForm from './VendorForm';

const { Column } = Table;
const { Search } = Input;

const VendorList = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const fetchVendors = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await vendorService.getAllVendors({
        page,
        limit: pagination.pageSize,
        search
      });

      // API interceptor returns response.data, so response is already unwrapped
      if (response?.success && response?.data) {
        setVendors(Array.isArray(response.data) ? response.data : []);

        setPagination(prev => ({
          ...prev,
          current: response?.pagination?.page || 1,
          total: response?.pagination?.total || 0
        }));
      } else {
        console.error('Unexpected response format:', response);
        setVendors([]);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      notification.error({
        message: 'Error',
        description: error?.error || error?.message || 'Failed to fetch vendors'
      });
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const handleTableChange = (pagination) => {
    fetchVendors(pagination.current, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchVendors(1, value);
  };

  const handleAdd = () => {
    setSelectedVendor(null);
    setIsDrawerVisible(true);
  };

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setIsDrawerVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await vendorService.deleteVendor(id);
      notification.success({
        message: 'Success',
        description: 'Vendor deleted successfully'
      });
      fetchVendors(pagination.current, searchText);
    } catch (error) {
      console.error('Delete vendor error:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to delete vendor.';
      notification.error({
        message: 'Delete Failed',
        description: errorMessage,
        duration: 5
      });
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
    setSelectedVendor(null);
  };

  const handleFormSubmit = () => {
    handleDrawerClose();
    fetchVendors(pagination.current, searchText);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row gutter={[20, 20]}>
        {[
          { title: 'Total Vendors', value: pagination.total || 0, icon: <ShopOutlined />, color: '#52c41a', bg: '#f6ffed' },
          { title: 'Total Invoices', value: vendors.reduce((sum, v) => sum + (v._count?.invoices || 0), 0), icon: <FileTextOutlined />, color: '#1890ff', bg: '#e6f7ff' },
          { title: 'Total Assets', value: vendors.reduce((sum, v) => sum + (v._count?.assets || 0), 0), icon: <BarcodeOutlined />, color: '#fa8c16', bg: '#fff7e6' },
        ].map((item, index) => (
          <Col xs={24} sm={8} key={index}>
            <Card bordered={false} style={{
              borderRadius: 16,
              background: item.bg,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{ position: 'absolute', right: -10, top: -10, opacity: 0.1, fontSize: 60, color: item.color }}>
                {item.icon}
              </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <Typography.Text type="secondary" style={{ fontSize: 13, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                  {item.title}
                </Typography.Text>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <Typography.Text style={{ fontSize: 28, fontWeight: 700, color: '#262626' }}>
                    {item.value}
                  </Typography.Text>
                </div>
                <div style={{ marginTop: 4 }}>
                  <Tag color={item.color} style={{ borderRadius: 10, border: 'none', fontSize: 10 }}>Live Data</Tag>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title="Vendors Management"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
          >
            New Vendor
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Search
            placeholder="Search vendors by name, mobile, email or GSTN..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={handleSearch}
            style={{ width: '100%', maxWidth: 500 }}
          />
        </div>
        <Table
          dataSource={vendors}
          loading={loading}
          rowKey="id"
          onChange={handleTableChange}
          pagination={pagination}
          scroll={{ x: 1200 }}
        >
          <Column
            title="Vendor"
            key="name"
            fixed="left"
            render={(record) => (
              <Space>
                <Avatar
                  size="large"
                  style={{ backgroundColor: '#52c41a' }}
                  icon={<ShopOutlined />}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{record.vendorName}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {record.vendorEmail}
                  </div>
                </div>
              </Space>
            )}
          />

          <Column
            title="Mobile"
            dataIndex="vendorMobile"
            key="vendorMobile"
          />

          <Column
            title="GSTN"
            dataIndex="gstn"
            key="gstn"
            render={(gstn) => gstn || <span style={{ color: '#ccc' }}>-</span>}
          />

          <Column
            title="Contact"
            key="contact"
            render={(record) => (
              <div>
                {record.contactName && (
                  <div style={{ fontSize: '12px' }}>{record.contactName}</div>
                )}
                {record.contactNumber && (
                  <div style={{ fontSize: '12px', color: '#666' }}>{record.contactNumber}</div>
                )}
              </div>
            )}
          />

          <Column
            title="Bank Accounts"
            key="bankAccounts"
            render={(record) => (
              <Tag color="blue">{record.bankDetails?.length || 0}</Tag>
            )}
          />

          <Column
            title="Stats"
            key="stats"
            render={(record) => (
              <Space>
                <Tag>{record._count?.invoices || 0} Invoices</Tag>
                <Tag color="orange">{record._count?.assets || 0} Assets</Tag>
              </Space>
            )}
          />

          <Column
            title="Actions"
            key="actions"
            fixed="right"
            render={(record) => (
              <Space>
                <Button
                  type="text"
                  icon={<EditOutlined style={{ color: '#1890ff' }} />}
                  onClick={() => handleEdit(record)}
                />
                <Popconfirm
                  title="Delete Vendor"
                  description="Are you sure you want to delete this vendor? This action cannot be undone."
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Space>
            )}
          />
        </Table>
      </Card>

      <Drawer
        title={selectedVendor ? 'Edit Vendor' : 'New Vendor'}
        width={800}
        onClose={handleDrawerClose}
        open={isDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        destroyOnClose
      >
        <VendorForm
          vendor={selectedVendor}
          onSuccess={handleFormSubmit}
          onCancel={handleDrawerClose}
        />
      </Drawer>
    </Space>
  );
};

export default VendorList;

